import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import { getDepartureOptionData } from '#/trip-planner/utils/date-time';
import { DEPARTURE_OPTION_SELECTOR } from '#/shared/constants';
import { setTripTimestamp } from '#/trip-planner/slice';

/**
 * Tools to keep the trip plan departure time in the redux state in sync with
 * the current time
 *
 * @param {number} updateInterval Interval at which the trip departure time is
 * updated to the current moment, pass `null` to prevent automatic updates
 * @returns {{departNow: boolean, setTripDepartTimeToNow: function}}
 */
const useDepartNow = (updateInterval = 5000) => {
  const { currentQuery } = useCurrentQuery();
  const { departNow } = getDepartureOptionData(
    currentQuery[DEPARTURE_OPTION_SELECTOR]
  );

  const dispatch = useDispatch();

  const setTripDepartTimeToNow = useCallback(() => {
    dispatch(setTripTimestamp(new Date().getTime()));
  }, [dispatch]);

  useEffect(() => {
    let intervalId = null;

    if (departNow && updateInterval) {
      // if the user is in leave "now" mode, update redux state's departure
      // time to the current time every at the supplied interval
      setTripDepartTimeToNow();
      intervalId = setInterval(setTripDepartTimeToNow, updateInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [departNow, setTripDepartTimeToNow, updateInterval]);

  return { departNow, setTripDepartTimeToNow };
};

export default useDepartNow;
